<template>
  <div class="badge-layer-editor">
    <div class="badge-layer-editor-toolbar">
      <span class="badge-layer-editor-tip">调整各项在底图上的位置与样式，右侧可实时预览</span>
      <el-button size="mini" @click="alignPhotoQr">头像与二维码底边对齐</el-button>
      <el-button size="mini" @click="resetDefaults">恢复默认布局</el-button>
    </div>
    <el-collapse v-model="activeKey" accordion class="badge-layer-collapse">
      <el-collapse-item
        v-for="meta in badgeLayerMetas"
        :key="meta.key"
        :name="meta.key"
        :title="meta.label">
        <el-form label-width="76px" size="mini" class="badge-layer-form">
          <el-row :gutter="8">
            <el-col :span="12">
              <el-form-item label="横坐标 X">
                <el-input-number
                  v-model="layerMap[meta.key].x"
                  :min="0"
                  :max="maxX"
                  controls-position="right"
                  style="width: 100%;"
                  @change="emitChange" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="纵坐标 Y">
                <el-input-number
                  v-model="layerMap[meta.key].y"
                  :min="0"
                  :max="maxY"
                  controls-position="right"
                  style="width: 100%;"
                  @change="meta.key === 'photo' ? onPhotoBoxChange : emitChange" />
              </el-form-item>
            </el-col>
          </el-row>

          <template v-if="meta.type === 'image'">
            <el-row :gutter="8">
              <el-col :span="12">
                <el-form-item label="宽度">
                  <el-input-number
                    v-model="layerMap[meta.key].w"
                    :min="20"
                    :max="maxX"
                    controls-position="right"
                    style="width: 100%;"
                    @change="onPhotoBoxChange" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="高度">
                  <el-input-number
                    v-model="layerMap[meta.key].h"
                    :min="20"
                    :max="maxY"
                    controls-position="right"
                    style="width: 100%;"
                    @change="onPhotoBoxChange" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item label="圆角">
              <el-input-number
                v-model="layerMap[meta.key].borderRadius"
                :min="0"
                :max="40"
                controls-position="right"
                style="width: 120px;"
                @change="emitChange" />
            </el-form-item>
          </template>

          <template v-else-if="meta.type === 'text'">
            <el-row :gutter="8">
              <el-col :span="12">
                <el-form-item label="字号">
                  <el-input-number
                    v-model="layerMap[meta.key].fontSize"
                    :min="10"
                    :max="180"
                    controls-position="right"
                    style="width: 100%;"
                    @change="emitChange" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="粗细">
                  <el-select v-model="layerMap[meta.key].fontWeight" style="width: 100%;" @change="emitChange">
                    <el-option label="常规" value="normal" />
                    <el-option label="加粗" value="bold" />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item label="颜色">
              <el-color-picker v-model="layerMap[meta.key].color" size="small" @change="emitChange" />
            </el-form-item>
          </template>

          <template v-else-if="meta.type === 'qrcode'">
            <el-form-item label="边长">
              <el-input-number
                v-model="layerMap[meta.key].size"
                :min="40"
                :max="Math.max(canvasWidth, canvasHeight)"
                controls-position="right"
                style="width: 120px;"
                @change="emitChange" />
            </el-form-item>
          </template>
        </el-form>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
/**
 * 校徽样式图层编辑器：调整各图层坐标、字号、颜色等，v-model 绑定 LayersJson。
 * 最后更新：2026-05-27
 */
import {
  badgeCanvasWidth,
  badgeCanvasHeight,
  badgeLayerMetas,
  parseBadgeLayers,
  stringifyBadgeLayers,
  defaultBadgeLayersJson,
  alignPhotoQrBottom,
  getDefaultBadgeLayerMap
} from './badgeStyleLayerDefaults'

export default {
  name: 'BadgeStyleLayerEditor',
  props: {
    value: { type: String, default: '' },
    canvasWidth: { type: Number, default: badgeCanvasWidth },
    canvasHeight: { type: Number, default: badgeCanvasHeight }
  },
  data() {
    return {
      badgeLayerMetas,
      layerMap: getDefaultBadgeLayerMap(),
      activeKey: 'photo',
      syncing: false
    }
  },
  computed: {
    maxX() {
      return this.canvasWidth || 750
    },
    maxY() {
      return this.canvasHeight || 240
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (this.syncing) return
        this.layerMap = parseBadgeLayers(val || defaultBadgeLayersJson)
      }
    }
  },
  methods: {
    /** 将图层映射序列化并同步到 v-model */
    emitChange() {
      this.syncing = true
      this.$emit('input', stringifyBadgeLayers(this.layerMap))
      this.$nextTick(() => {
        this.syncing = false
      })
    },
    /** 头像尺寸变更时自动对齐二维码底边 */
    onPhotoBoxChange() {
      alignPhotoQrBottom(this.layerMap)
      this.emitChange()
    },
    /** 手动触发头像与二维码底边对齐 */
    alignPhotoQr() {
      alignPhotoQrBottom(this.layerMap)
      this.emitChange()
      this.$message.success('已按头像底边对齐二维码')
    },
    /** 恢复案例默认图层布局 */
    resetDefaults() {
      this.layerMap = getDefaultBadgeLayerMap()
      this.emitChange()
      this.$message.success('已恢复默认图层布局')
    }
  }
}
</script>

<style scoped>
.badge-layer-editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}
.badge-layer-editor-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.badge-layer-collapse {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.badge-layer-collapse >>> .el-collapse-item__header {
  height: 40px;
  line-height: 40px;
  padding-left: 12px;
  font-size: 13px;
}
.badge-layer-form {
  padding: 4px 8px 8px;
}
.badge-layer-form >>> .el-form-item {
  margin-bottom: 8px;
}
</style>
